import React from 'react';
import { MapPin } from 'lucide-react';

/**
 * Компонент выбора местоположения для расчета налога
 */
const LocationSection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  // Список регионов России с налоговыми ставками
  const regions = [
    { id: 'moscow', name: 'Москва', baseTaxRate: 0.1 },
    { id: 'moscow_reg', name: 'Московская область', baseTaxRate: 0.1 },
    { id: 'spb', name: 'Санкт-Петербург', baseTaxRate: 0.1 },
    { id: 'tatarstan', name: 'Республика Татарстан', baseTaxRate: 0.1 },
    { id: 'krasnodar', name: 'Краснодарский край', baseTaxRate: 0.1 },
    { id: 'sverdlovsk', name: 'Свердловская область', baseTaxRate: 0.1 },
    { id: 'novosibirsk', name: 'Новосибирская область', baseTaxRate: 0.1 },
    { id: 'other', name: 'Другой регион', baseTaxRate: 0.1 }
  ];

  // Список муниципалитетов/районов для выбранного региона
  const getMunicipalities = (regionId) => {
    switch (regionId) {
      case 'moscow':
        return [
          { id: 'central', name: 'Центральный административный округ' },
          { id: 'northern', name: 'Северный административный округ' },
          { id: 'northeastern', name: 'Северо-Восточный административный округ' },
          { id: 'eastern', name: 'Восточный административный округ' },
          { id: 'southeastern', name: 'Юго-Восточный административный округ' },
          { id: 'southern', name: 'Южный административный округ' },
          { id: 'southwestern', name: 'Юго-Западный административный округ' },
          { id: 'western', name: 'Западный административный округ' },
          { id: 'northwestern', name: 'Северо-Западный административный округ' },
          { id: 'zelenogradsky', name: 'Зеленоградский административный округ' },
          { id: 'troitsky', name: 'Троицкий административный округ' },
          { id: 'novomoskovsky', name: 'Новомосковский административный округ' }
        ];
      case 'spb':
        return [
          { id: 'admiralteysky', name: 'Адмиралтейский район' },
          { id: 'vasileostrovsky', name: 'Василеостровский район' },
          { id: 'vyborgsky', name: 'Выборгский район' },
          { id: 'kalininsky', name: 'Калининский район' },
          { id: 'kirovsky', name: 'Кировский район' },
          { id: 'kolpinsky', name: 'Колпинский район' },
          { id: 'krasnoselsky', name: 'Красносельский район' },
          { id: 'kronshtadtsky', name: 'Кронштадтский район' },
          { id: 'moskovsky', name: 'Московский район' },
          { id: 'nevsky', name: 'Невский район' },
          { id: 'petrogradsky', name: 'Петроградский район' },
          { id: 'primorsky', name: 'Приморский район' },
          { id: 'pushkinsky', name: 'Пушкинский район' },
          { id: 'frunzensky', name: 'Фрунзенский район' },
          { id: 'tsentralny', name: 'Центральный район' }
        ];
      case 'moscow_reg':
        return [
          { id: 'khimki', name: 'Химки' },
          { id: 'krasnogorsk', name: 'Красногорск' },
          { id: 'odintsovo', name: 'Одинцово' },
          { id: 'mytishchi', name: 'Мытищи' },
          { id: 'domodedovo', name: 'Домодедово' },
          { id: 'lyubertsy', name: 'Люберцы' },
          { id: 'other_moscow_reg', name: 'Другой город/район области' }
        ];
      default:
        return [{ id: 'default', name: 'Выберите населенный пункт' }];
    }
  };

  // Получаем список муниципалитетов для выбранного региона
  const municipalities = getMunicipalities(formData.region);

  return (
    <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 p-5 rounded-lg border border-amber-200 dark:border-amber-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-amber-500" />
        Местоположение
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Регион */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Регион
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.region ? 'input-error' : ''}`}
          >
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.region}
            </p>
          )}
        </div>

        {/* Муниципалитет/Район */}
        <div>
          <label htmlFor="municipality" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Муниципалитет/Район
          </label>
          <select
            id="municipality"
            name="municipality"
            value={formData.municipality}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.municipality ? 'input-error' : ''}`}
          >
            {municipalities.map((municipality) => (
              <option key={municipality.id} value={municipality.id}>
                {municipality.name}
              </option>
            ))}
          </select>
          {errors.municipality && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.municipality}
            </p>
          )}
        </div>
      </div>

      {/* Информационный блок о региональных особенностях */}
      <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-sm text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
        <p className="font-medium">Региональные особенности налогообложения:</p>
        <ul className="list-disc list-inside mt-1 space-y-1">
          {formData.region === 'moscow' && (
            <>
              <li>В Москве действуют повышенные ставки для элитной недвижимости и дорогих автомобилей</li>
              <li>Для автомобилей с мощностью двигателя более 250 л.с. применяется повышенная ставка от 150 руб/л.с.</li>
              <li>Для апартаментов ставка выше, чем для обычных квартир</li>
            </>
          )}
          {formData.region === 'spb' && (
            <>
              <li>В Санкт-Петербурге применяется дифференцированная шкала ставок для автомобилей</li>
              <li>Для исторических зданий в центре города могут действовать особые правила налогообложения</li>
            </>
          )}
          {formData.region === 'moscow_reg' && (
            <>
              <li>В Московской области ставки ниже, чем в Москве для аналогичных объектов</li>
              <li>В некоторых муниципалитетах действуют дополнительные льготы для местных жителей</li>
            </>
          )}
          {formData.region !== 'moscow' && formData.region !== 'spb' && formData.region !== 'moscow_reg' && (
            <li>Каждый регион устанавливает собственные ставки транспортного налога и налога на имущество в пределах, установленных федеральным законодательством</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default LocationSection;